import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import {
  Search,
  GitBranch,
  Star,
  GitFork,
  AlertCircle,
  Archive,
  ExternalLink,
  Filter,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { apiService } from "../services/api";
import { Repository } from "../types";
import Pagination from "../components/Pagination";

export default function RepositoriesPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [archivedFilter, setArchivedFilter] = useState<string>("");
  const [pushedAtFilter, setPushedAtFilter] = useState<string>("");
  const [noContributorsFilter, setNoContributorsFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>("desc");
  const [sortField, setSortField] = useState<
    "pushedAt" | "contributors" | "lastCommitAt"
  >("lastCommitAt");

  const { data: reposData, isLoading } = useQuery(
    [
      "repositories",
      appliedSearch,
      ownerFilter,
      languageFilter,
      archivedFilter,
      noContributorsFilter,
    ],
    () =>
      apiService.getRepositories({
        search: appliedSearch || undefined,
        owner: ownerFilter || undefined,
        language: languageFilter || undefined,
        archived: archivedFilter === "" ? undefined : archivedFilter === "true",
        noContributors: noContributorsFilter || undefined,
        limit: 10000,
      }),
    { keepPreviousData: true },
  );

  const allRepos: Repository[] = reposData?.data?.data?.repositories || [];

  const filteredRepos = useMemo(() => {
    let repos = allRepos;

    if (pushedAtFilter) {
      const now = Date.now();
      const daysMap: Record<string, number> = {
        "7": 7,
        "30": 30,
        "90": 90,
        "180": 180,
        "365": 365,
      };
      const days = daysMap[pushedAtFilter];
      if (days) {
        const cutoff = now - days * 24 * 60 * 60 * 1000;
        repos = repos.filter(
          (r) => r.pushedAt && new Date(r.pushedAt).getTime() >= cutoff,
        );
      }
    }

    if (sortOrder !== null) {
      repos = [...repos].sort((a, b) => {
        if (sortField === "pushedAt") {
          const aDate = a.pushedAt ? new Date(a.pushedAt).getTime() : 0;
          const bDate = b.pushedAt ? new Date(b.pushedAt).getTime() : 0;
          return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
        } else if (sortField === "lastCommitAt") {
          const aDate = a.lastCommitAt ? new Date(a.lastCommitAt).getTime() : 0;
          const bDate = b.lastCommitAt ? new Date(b.lastCommitAt).getTime() : 0;
          return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
        } else {
          const aCount = a.recentContributorCount ?? 0;
          const bCount = b.recentContributorCount ?? 0;
          return sortOrder === "asc" ? aCount - bCount : bCount - aCount;
        }
      });
    }

    return repos;
  }, [allRepos, sortOrder, sortField, pushedAtFilter]);

  const totalPages = Math.ceil(filteredRepos.length / itemsPerPage);
  const paginatedRepos = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRepos.slice(start, start + itemsPerPage);
  }, [filteredRepos, currentPage, itemsPerPage]);

  const owners = useMemo(() => {
    const set = new Set(allRepos.map((r) => r.owner));
    return Array.from(set).sort();
  }, [allRepos]);

  const languages = useMemo(() => {
    const set = new Set(allRepos.map((r) => r.language).filter(Boolean));
    return Array.from(set).sort() as string[];
  }, [allRepos]);

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const toggleSort = (
    field: "pushedAt" | "contributors" | "lastCommitAt" = "pushedAt",
  ) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder("desc");
    } else {
      setSortOrder((prev) =>
        prev === null ? "desc" : prev === "desc" ? "asc" : null,
      );
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setOwnerFilter("");
    setLanguageFilter("");
    setArchivedFilter("");
    setPushedAtFilter("");
    setNoContributorsFilter(false);
    setSortOrder(null);
    setSortField("pushedAt");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    appliedSearch ||
    ownerFilter ||
    languageFilter ||
    archivedFilter !== "" ||
    pushedAtFilter !== "" ||
    noContributorsFilter;

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Repositories
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Browse GitHub repositories synced to ProdAtlas.
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search repositories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="input pl-10 w-full"
              />
            </div>
            <button onClick={handleSearch} className="btn btn-primary">
              Search
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-secondary flex items-center gap-2 ${hasActiveFilters ? "ring-2 ring-primary-500" : ""}`}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 bg-primary-500 text-white text-xs rounded-full px-1.5 py-0.5">
                !
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Owner
                </label>
                <select
                  value={ownerFilter}
                  onChange={(e) => {
                    setOwnerFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="input w-full"
                >
                  <option value="">All Owners</option>
                  {owners.map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Language
                </label>
                <select
                  value={languageFilter}
                  onChange={(e) => {
                    setLanguageFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="input w-full"
                >
                  <option value="">All Languages</option>
                  {languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={archivedFilter}
                  onChange={(e) => {
                    setArchivedFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="input w-full"
                >
                  <option value="">All</option>
                  <option value="false">Active</option>
                  <option value="true">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Pushed
                </label>
                <select
                  value={pushedAtFilter}
                  onChange={(e) => {
                    setPushedAtFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="input w-full"
                >
                  <option value="">Any time</option>
                  <option value="7">&lt; 1 week</option>
                  <option value="30">&lt; 1 month</option>
                  <option value="90">&lt; 3 months</option>
                  <option value="180">&lt; 6 months</option>
                  <option value="365">&lt; 1 year</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contributors
                </label>
                <label className="flex items-center gap-2 h-[38px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noContributorsFilter}
                    onChange={(e) => {
                      setNoContributorsFilter(e.target.checked);
                      setCurrentPage(1);
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    No contributors
                  </span>
                </label>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : filteredRepos.length === 0 ? (
        <div className="card p-12 text-center">
          <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No repositories found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? "Try adjusting your search or filters."
              : "No repositories have been synced yet."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Showing {filteredRepos.length}
            {filteredRepos.length !== allRepos.length &&
              ` of ${allRepos.length}`}{" "}
            repositories
          </p>
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[30%]">
                    Repository
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Language
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Stats
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => toggleSort("pushedAt")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Last Pushed
                      {sortField !== "pushedAt" || sortOrder === null ? (
                        <ArrowUpDown className="h-3.5 w-3.5" />
                      ) : sortOrder === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => toggleSort("lastCommitAt")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Last Commit
                      {sortField !== "lastCommitAt" || sortOrder === null ? (
                        <ArrowUpDown className="h-3.5 w-3.5" />
                      ) : sortOrder === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => toggleSort("contributors")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Contributors
                      {sortField !== "contributors" || sortOrder === null ? (
                        <ArrowUpDown className="h-3.5 w-3.5" />
                      ) : sortOrder === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedRepos.map((repo) => (
                  <tr
                    key={repo.fullName}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-6 py-4 w-[30%]">
                      <div>
                        <Link
                          to={`/repositories/${repo.owner}/${repo.name}`}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {repo.fullName}
                        </Link>
                        {repo.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                            {repo.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {repo.language ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {repo.language}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          repo.visibility === "public"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : repo.visibility === "internal"
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {repo.visibility}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1" title="Stars">
                          <Star className="h-3.5 w-3.5" />
                          {repo.starsCount}
                        </span>
                        <span className="flex items-center gap-1" title="Forks">
                          <GitFork className="h-3.5 w-3.5" />
                          {repo.forksCount}
                        </span>
                        <span
                          className="flex items-center gap-1"
                          title="Open Issues"
                        >
                          <AlertCircle className="h-3.5 w-3.5" />
                          {repo.openIssues}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {repo.pushedAt
                        ? new Date(repo.pushedAt).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {repo.lastCommitAt
                        ? new Date(repo.lastCommitAt).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {repo.recentContributorCount ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {repo.archived ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                          <Archive className="h-3 w-3" />
                          Archived
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <a
                        href={repo.htmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Open in GitHub"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRepos.length > itemsPerPage && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredRepos.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
